/***
 * Configuration for Clients Routes
 *
 * Client Routes
 * Site Routes
 * Sector Routes
 * Activity Routes
 ***/

/* ----- Views ----- */

// Client
import Clients from "@/pages/Clients/Clients";
import ClientDetails from "@/pages/Clients/ClientDetails";
import InformationsEdit from "@/pages/Clients/Informations/InformationsEdit";
import ContactEdit from "@/pages/Clients/Contacts/ContactEdit";
import NotesEdit from "@/pages/Clients/Notes/NotesEdit";
import MessageEdit from "@/pages/Clients/Notes/MessageEdit";

// Site
import SiteDetails from "@/pages/Sites/SiteDetails";
import SiteEdit from "@/pages/Sites/Informations/InformationsEdit";
import SiteDocumentPublicAdd from "@/pages/Sites/Documents/DocumentPublicAdd";
import SiteDocumentPrivateAdd from "@/pages/Sites/Documents/DocumentPrivateAdd";
import SiteDevicesEdit from "@/pages/Sites/Devices/DevicesEdit";
import SiteDevicesProductsEdit from "@/pages/Sites/Devices/Products/DevicesProductsEdit";
import SiteDeviceGroupEdit from "@/pages/Sites/DeviceGroups/DeviceGroupEdit";
import ContractAdd from "@/pages/Contracts/ContractAdd";
import SitePlanAdd from "@/pages/Sites/Devices/Plans/PlanAdd";
import SitePlanEdit from "@/pages/Sites/Devices/Plans/PlanEdit";

// Sector
import Sectors from "@/pages/Configuration/Sectors";
import SectorAdd from "@/pages/Configuration/Sector/SectorAdd";
import SectorEdit from "@/pages/Configuration/Sector/SectorEdit";

// Activity
import Activities from "@/pages/Configuration/Activities";
import ActivityAdd from "@/pages/Configuration/Activity/ActivityAdd";
import ActivityEdit from "@/pages/Configuration/Activity/ActivityEdit";

// Other
import Sites from "@/pages/Sites/Sites";
import Technicians from "@/pages/Clients/Technicians/Technicians";

/* ----- ----- ----- ----- ----- ----- ----- */

// Utils
import { generateParentsList } from "@/router/utils";

// Parents List
const parentsList = {
    clientsIndex: { label: 'Liste des clients actifs', name: 'Clients' },
    clientDetails: {
        label: 'Détails d\'un client',
        name: 'Client Details',
        params: { name: 'id', model: 'Client', column: 'name' },
    },
    siteDetails: {
        label: 'Détails d\'un site',
        name: 'Site Details',
        params: { name: 'idSite', model: 'Site', column: 'name' },
    },
    sectorsIndex: { label: 'Liste des secteurs', name: 'Sectors' },
    activitiesIndex: { label: 'Liste des activités', name: 'Activities' },
}

const clientRoutes = [
    {
        path: 'clients',
        name: 'Clients',
        component: Clients,
        meta: {
            route: {
                label: 'Liste des clients actifs'
            }
        },
    },
    {
        path: 'clients/:id',
        name: 'Client Details',
        component: ClientDetails,
        meta: {
            route: {
                label: 'Détails d\'un client',
                params: { name: 'id', model: 'Client', column: 'name' },
            },
            parents: generateParentsList(parentsList, ['clientsIndex']),
        },
    },
    {
        path: 'clients/:id/edit',
        name: 'Client Informations Edit',
        component: InformationsEdit,
        meta: {
            route: {
                label: 'Modification du client'
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails']),
        }
    },
    {
        path: 'clients/:id/contact/:idContact',
        name: 'Client Contact Edit',
        component: ContactEdit,
        meta: {
            route: {
                label: 'Modification d\'un contact',
                params: { name: 'idContact', model: 'Contact', column: 'full_name' }
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails']),
        }
    },
    {
        path: 'clients/:id/notes/edit',
        name: 'Client Notes Edit',
        component: NotesEdit,
        meta: {
            route: {
                label: 'Modification des notes'
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails']),
        }
    },
    {
        path: 'clients/:id/message/edit',
        name: 'Client Message Edit',
        component: MessageEdit,
        meta: {
            route: {
                label: 'Modification du message personnel'
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails']),
        }
    },
]

const siteRoutes = [
    {
        path: 'clients/:id/site/:idSite',
        name: 'Site Details',
        component: SiteDetails,
        meta: {
            route: {
                label: 'Détails d\'un site',
                params: { name: 'idSite', model: 'Site', column: 'name' }
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails']),
        },
    },
    {
        path: 'clients/:id/site/:idSite/edit',
        name: 'Site Informations Edit',
        component: SiteEdit,
        meta: {
            route: {
                label: 'Modification du site'
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails', 'siteDetails']),
        },
    },
    {
        path: 'clients/:id/site/:idSite/document_public/create',
        name: 'Add Site Document Public',
        component: SiteDocumentPublicAdd,
        meta: {
            route: {
                label: 'Ajout d\'un document public'
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails', 'siteDetails']),
        },
    },
    {
        path: 'clients/:id/site/:idSite/document_private/create',
        name: 'Add Site Document Private',
        component: SiteDocumentPrivateAdd,
        meta: {
            route: {
                label: 'Ajout d\'un document privé'
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails', 'siteDetails']),
        },
    },
    {
        path: 'clients/:id/site/:idSite/edit_devices',
        name: 'Site Devices Edit',
        component: SiteDevicesEdit,
        meta: {
            route: {
                label: 'Gestion des dispositifs'
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails', 'siteDetails']),
        },
    },
    {
        path: 'clients/:id/site/:idSite/devices_products',
        name: 'Site Devices Products Edit',
        component: SiteDevicesProductsEdit,
        meta: {
            route: {
                label: 'Gestion des produits'
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails', 'siteDetails']),
        },
    },
    {
        path: 'clients/:id/site/:idSite/device_groups/:deviceGroupId/edit',
        name: 'Site Device Group Edit',
        component: SiteDeviceGroupEdit,
        meta: {
            route: {
                label: 'Modification d\'un groupement',
                shortLabel: 'Groupement : ',
                params: { name: 'deviceGroupId', model: 'DeviceGroup', column: 'name' }
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails', 'siteDetails']),
        },
    },
    {
        path: 'clients/:id/site/:idSite/contract/create',
        name: 'Add Site Contract',
        component: ContractAdd,
        meta: {
            route: {
                label: 'Ajout d\'un contrat'
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails', 'siteDetails']),
        },
    },
    {
        path: 'clients/:id/site/:idSite/plans/create',
        name: 'Site Plan Add',
        component: SitePlanAdd,
        meta: {
            route: {
                label: 'Ajout d\'un plan'
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails', 'siteDetails']),
        },
    },
    {
        path: 'clients/:id/site/:idSite/plans/:documentId/edit',
        name: 'Site Plan Edit',
        component: SitePlanEdit,
        meta: {
            route: {
                label: 'Modification d\'un plan',
                shortLabel: 'Plan : ',
                params: { name: 'documentId', model: 'SiteDocument', column: 'name' }
            },
            parents: generateParentsList(parentsList, ['clientsIndex', 'clientDetails', 'siteDetails']),
        },
    },
]

const sectorRoutes = [
    {
        path: 'sectors',
        name: 'Sectors',
        component: Sectors,
        meta: {
            route: {
                label: 'Liste des secteurs'
            }
        },
    },
    {
        path: 'sectors/create',
        name: 'Add Sector',
        component: SectorAdd,
        meta: {
            route: {
                label: 'Ajout d\'un secteur'
            },
            parents: generateParentsList(parentsList, ['sectorsIndex']),
        },
    },
    {
        path: 'sectors/:id/edit',
        name: 'Edit Sector',
        component: SectorEdit,
        meta: {
            route: {
                label: 'Modification d\'un secteur',
                params: { name: 'id', model: 'Sector', column: 'name' },
            },
            parents: generateParentsList(parentsList, ['sectorsIndex']),
        },
    },
]

const activityRoutes = [
    {
        path: 'activities',
        name: 'Activities',
        component: Activities,
        meta: {
            route: {
                label: 'Liste des activités'
            }
        },
    },
    {
        path: 'activities/create',
        name: 'Add Activity',
        component: ActivityAdd,
        meta: {
            route: {
                label: 'Ajout d\'une activité'
            },
            parents: generateParentsList(parentsList, ['activitiesIndex']),
        },
    },
    {
        path: 'activities/:id/edit',
        name: 'Edit Activity',
        component: ActivityEdit,
        meta: {
            route: {
                label: 'Modification d\'un activité',
                params: { name: 'id', model: 'Activity', column: 'name' },
            },
            parents: generateParentsList(parentsList, ['activitiesIndex']),
        },
    },
]

const otherRoutes = [
    {
        path: 'sites',
        name: 'Sites',
        component: Sites,
        meta: {
            route: {
                label: 'Liste des sites'
            }
        }
    },
    {
        path: 'technician_assignment',
        name: 'Technicians Assignment',
        component: Technicians,
        meta: {
            route: {
                label: 'Affectation des techniciens'
            }
        }
    },
]

const routes = [...clientRoutes, ...siteRoutes, ...sectorRoutes, ...activityRoutes, ...otherRoutes]
export default routes